import React, { useContext, useState } from 'react'
import { Button, ListMenu, ListMenuButton, Modal,
   TabMenu, TabMenuButton, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { AddIcon, ArrowDownIcon, CloseIcon, MoreIcon } from 'assets/icons'
import initiativePrivacyConstants from 'config/constants/initiativePrivacy'
import { initiativeUrl } from 'config/constants/urls'
import { useInitiativeList, useOrganization, useUpdateInitiative } from 'src/core/graphql/hooks'
import InitiativeAppMoreControl from 'src/sites/kits/Initiative/components/AppMoreControl'
import ObjectDeleteConfirm from 'src/sites/kits/Object/components/DeleteConfirm'
import { BackButton, EmptyContentBlock, Title, Titlebar } from 'src/sites/kits/UI'
import { useEventSessionDateGrouping, useLocationQuery } from 'src/sites/kits/Utils'
import ModalRoute from 'src/sites/kits/Utils/ModalRoute'
import Popover from 'src/sites/kits/Utils/Popover'
import ScrollView from 'src/sites/kits/Utils/ScrollView'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'
import SessionListItem from '~/components/SessionListItem'
import SessionCreate from './SessionCreate'
import Session from './Session'

const adminOnlyPrivacyConstants = initiativePrivacyConstants.find(p => p.value === 'admin')

const InitiativeSchedule = ({
   paddingLeft,
   paddingRight,
   showMobileBackButton
}) => {
   const history = useHistory()
   const match = useRouteMatch()
   const { labelId } = useLocationQuery()
   const { selectedDateKey: initialSelectedDateKey } = useParams()
   const [createSessionModalIsOpen, setCreateSessionModalIsOpen] = useState(false)
   const [initiativeForDeletion, setInitiativeForDeletion] = useState(null)
   const [labelPickerModalIsOpen, setLabelPickerModalIsOpen] = useState(false)

   const { organization } = useOrganization()
   const sessionInitiativeType = organization.initiativeTypes.find(t => t.class === 'session')
   const { initiative: event, refetch } = useContext(InitiativeHierarchyContext)
   const { updateInitiative } = useUpdateInitiative()
   const { initiativeList: sessionList, networkStatus } = useInitiativeList({
      archived: 'exclude',
      drafts: 'include',
      parentInitiativeId: event.id,
      sortBy: 'startDateAsc',
      timezoneOffset: new Date().getTimezoneOffset(),
      typeIds: [sessionInitiativeType.id]
   })

   // Because sessionList is already ordered, we do not need to worry about the order
   // of the day labels nor the order of sessions each day.
   const [sessionDates, sessionsGroupedByDate] = useEventSessionDateGrouping(sessionList)

   const baseUrl = initialSelectedDateKey
      ? match.url.split('/').slice(0, -1).join('/')
      : match.url

   let selectedDateKey = null
   let selectedDateSessions = []
   if (networkStatus === 7) {
      try {
         selectedDateKey = initialSelectedDateKey || Object.keys(sessionsGroupedByDate)[0]
         selectedDateSessions = selectedDateKey ? sessionsGroupedByDate[selectedDateKey] : []
      } catch (err) {} // eslint-disable-line no-empty
   }

   const scheduleHasLabels = event.appsScheduleLabels.length > 0
   const activeLabelObj = event.appsScheduleLabels.find(label => label.id === labelId)
   // If a labelId has been passed to the query params, then filter the sessions
   let labelIdQueryString = ''
   if (labelId) {
      labelIdQueryString = `?labelId=${labelId}`
      selectedDateSessions = selectedDateSessions.filter(session => (
         session.labels.some(label => label.id === labelId)
      ))
   }

   if (!initialSelectedDateKey && selectedDateKey && networkStatus === 7) {
      history.replace(`${baseUrl}/${selectedDateKey}`)
      return null
   }

   const handleToggleSchedulePublished = async () => {
      await updateInitiative({ id: event.id }, {
         appsSchedulePublished: !event.appsSchedulePublished
      })

      refetch()
   }

   return (
      <>
         {!event.appsSchedulePublished && (
            <View
               float="left"
               width="100%"
               padding="15px 20px"
               backgroundColor={adminOnlyPrivacyConstants.backgroundColor}
               textAlign="center">
               <Text size="1.5" color="#524439">
                  This Schedule page is currently in Admin-only Mode
                  <View
                     onClick={handleToggleSchedulePublished}
                     marginLeft="10px"
                     display="inline-block">
                     <u style={{ color: '#524439', cursor: 'pointer' }}>
                        <b>Publish now</b>
                     </u>
                  </View>
               </Text>
            </View>
         )}
         <Titlebar paddingLeft={paddingLeft} paddingRight={paddingRight}>
            <View display="flex">
               {showMobileBackButton && <BackButton linkTo={initiativeUrl(event)} />}
               <Title size="md">Schedule</Title>
            </View>
            <View display="flex">
               <Button
                  onClick={() => setCreateSessionModalIsOpen(true)}
                  color="#000"
                  textColor="#fff"
                  name="Add Session"
                  padding="15px"
                  rounded>
                  <View marginRight="10px">
                     <AddIcon width="12px" height="12px" color="#fff" strokeWidth="2px" />
                  </View>
               </Button>
               <InitiativeAppMoreControl
                  appName="Schedule"
                  appEnabledFieldName="appsScheduleEnabled"
                  appPublishedFieldName="appsSchedulePublished"
               />
            </View>
         </Titlebar>
         {sessionDates.length > 0 && (
            <View padding="10px" backgroundColor="#f5f5f5">
               <Text size="1" color="#888" weight="medium">
                  {`All times displayed in this schedule are in ${event.location.timezoneId} `}
                  {event.timezoneAbbr && `(${event.timezoneAbbr})`}
               </Text>
            </View>
         )}
         <View
            display="block[a-d] none[e-f]"
            width="100%">
            <TabMenu>
               {sessionDates.map(sessionDate => (
                  <TabMenuButton
                     key={sessionDate.dateKey}
                     name={sessionDate.nameShort}
                     linkTo={`${baseUrl}/${sessionDate.dateKey}`}
                  />
               ))}
            </TabMenu>
            {sessionDates.length > 0 && scheduleHasLabels && (
               <>
                  <View
                     onClick={() => setLabelPickerModalIsOpen(true)}
                     display="flex"
                     justifyContent="space-between"
                     alignItems="center"
                     float="left"
                     width="100%"
                     padding="9px 12px"
                     backgroundColor="#eee"
                     borderRadius="3px"
                     marginTop="12px">
                     <Text size="1.5" color="#666">
                        Showing:
                        {activeLabelObj
                           ? ` ${activeLabelObj.name}`
                           : ' All Topics'
                        }
                     </Text>
                     <ArrowDownIcon
                        width="10px"
                        height="10px"
                        color="#666"
                        strokeWidth="4px"
                     />
                  </View>
                  <Modal
                     borderRadius="6px"
                     width="300px"
                     height="300px"
                     onCloseTrigger={() => setLabelPickerModalIsOpen(false)}
                     open={labelPickerModalIsOpen}>
                     <View
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        padding="0px 18px"
                        width="100%"
                        height="42px"
                        borderBottom="1px solid #eee">
                        <Text weight="medium" color="#333" size="2">
                           Filter Topics:
                        </Text>
                        <View onClick={() => setLabelPickerModalIsOpen(false)}>
                           <CloseIcon
                              width="15px"
                              height="15px"
                              strokeWidth="2px"
                              color="#555"
                           />
                        </View>
                     </View>
                     {/* Note: This is not optimal, but we make scrollview
                        conditional so it does not interfere with other View
                        components with scroll behaviour */}
                     {labelPickerModalIsOpen && (
                        <ScrollView
                           onClick={() => setLabelPickerModalIsOpen(false)}
                           width="100%"
                           height="100%"
                           scroll="on">
                           <ListMenu
                              activeBackgroundColor="#eee"
                              activeTextColor="#333"
                              buttonBorderRadius="4px"
                              buttonSize="md"
                              buttonTextSize="1.5"
                              buttonPaddingHorizontal="18px"
                              dividerLineStyle="none">
                              <ListMenuButton
                                 isActive={!labelId}
                                 name="All"
                                 linkTo={match.url}
                                 leftDetail={(
                                    <View
                                       width="9px"
                                       height="9px"
                                       borderRadius="50%"
                                       border="1px solid #aaa"
                                    />
                                 )}
                              />
                              {event.appsScheduleLabels.map(label => (
                                 <ListMenuButton
                                    key={label.id}
                                    isActive={label.id === labelId}
                                    name={label.name}
                                    linkTo={`${match.url}?labelId=${label.id}`}
                                    leftDetail={(
                                       <View
                                          width="9px"
                                          height="9px"
                                          borderRadius="50%"
                                          backgroundColor={label.color}
                                       />
                                    )}
                                 />
                              ))}
                           </ListMenu>
                        </ScrollView>
                     )}
                  </Modal>
               </>
            )}
         </View>
         {sessionDates.length === 0 && (
            <EmptyContentBlock
               message={`No sessions have been added yet to the ${event.type.nameSingular} Schedule`}
            />
         )}
         <View display="flex" width="100%">
            <View
               display="none[a-d] block[e-f]"
               width="180px"
               flex="0 0 auto"
               paddingTop="12px"
               paddingRight="30px">
               {scheduleHasLabels && sessionDates.length > 1 && (
                  <View margin="12px 12px 6px 12px">
                     <Text size="0.9" weight="bold" color="#aaa" transform="uppercase">
                        Select Date
                     </Text>
                  </View>
               )}
               <ListMenu
                  activeBackgroundColor="#222"
                  activeTextColor="#fff"
                  buttonBorderRadius="4px"
                  buttonSize="sm"
                  buttonTextSize="1.5"
                  buttonPaddingHorizontal="12px"
                  dividerLineStyle="none"
                  showActiveArrow>
                  {sessionDates.map(sessionDate => (
                     <ListMenuButton
                        key={sessionDate.dateKey}
                        isActive={selectedDateKey === sessionDate.dateKey}
                        name={sessionDate.nameFull}
                        linkTo={`${baseUrl}/${sessionDate.dateKey}${labelIdQueryString}`}
                     />
                  ))}
               </ListMenu>
               {sessionDates.length > 0 && scheduleHasLabels && (
                  <View
                     float="left"
                     borderTop="1px solid #eee"
                     margin="12px 0px">
                     <View margin="12px 12px 6px 12px">
                        <Text size="0.9" weight="bold" color="#aaa" transform="uppercase">
                           Filter by Topic
                        </Text>
                     </View>
                     <ListMenu
                        activeBackgroundColor="#eee"
                        activeTextColor="#333"
                        buttonBorderRadius="4px"
                        buttonSize="sm"
                        buttonTextSize="1"
                        buttonPaddingHorizontal="12px"
                        dividerLineStyle="none">
                        <ListMenuButton
                           isActive={!labelId}
                           name="All"
                           linkTo={match.url}
                           leftDetail={(
                              <View
                                 width="9px"
                                 height="9px"
                                 borderRadius="50%"
                                 border="1px solid #aaa"
                              />
                           )}
                        />
                        {event.appsScheduleLabels.map(label => (
                           <ListMenuButton
                              key={label.id}
                              isActive={label.id === labelId}
                              name={label.name}
                              linkTo={`${match.url}?labelId=${label.id}`}
                              leftDetail={(
                                 <View
                                    width="9px"
                                    height="9px"
                                    borderRadius="50%"
                                    backgroundColor={label.color}
                                 />
                              )}
                           />
                        ))}
                     </ListMenu>
                  </View>
               )}
            </View>
            <View flex="1 1 auto">
               <Popover.Provider>
                  {selectedDateSessions && selectedDateSessions.map((session, index) => (
                     <SessionListItem
                        key={session.id}
                        borderStyle={index === 0 ? 'none' : undefined}
                        endTime={session.endTime}
                        labels={session.labels}
                        location={session.location?.name}
                        linkTo={`${baseUrl}/${selectedDateKey}/session/${session.slug}`}
                        name={session.name}
                        startTime={session.startTime}
                        action={(
                           <Popover.Anchor meta={session} tabIndex={index}>
                              <View marginRight="10px">
                                 <MoreIcon
                                    width="24px"
                                    height="24px"
                                    color="#aaa"
                                 />
                              </View>
                           </Popover.Anchor>
                        )}
                     />
                  ))}
                  <Popover.View
                     anchorOriginHorizontal="right"
                     anchorOriginVertical="top"
                     backgroundColor="#fff"
                     borderRadius="3px"
                     width="150px"
                     margin="10px">
                     {session => (
                        <View
                           float="left"
                           width="100%"
                           backgroundColor="#fff"
                           borderRadius="3px"
                           boxShadow="3px 3px 5px rgba(0,0,0,0.1)">
                           <ListMenu buttonSize="sm" buttonTextColor="#333">
                              <ListMenuButton
                                 linkTo={`${baseUrl}/${selectedDateKey}/session/${session.slug}/edit`}
                                 name="Edit Session"
                              />
                              <ListMenuButton
                                 onClick={() => setInitiativeForDeletion({
                                    // eslint-disable-next-line max-len
                                    activeSubInitiativeTypeNames: session.activeSubInitiativeTypeNames,
                                    id: session.id,
                                    name: session.name
                                 })}
                                 name="Delete Session"
                              />
                           </ListMenu>
                        </View>
                     )}
                  </Popover.View>
               </Popover.Provider>
            </View>
         </View>
         <Switch>
            <ModalRoute path={`${baseUrl}/${selectedDateKey}/session/:sessionSlug`}>
               {({ open, onCloseComplete, onCloseTrigger }) => (
                  <Session
                     modalOnCloseComplete={onCloseComplete}
                     modalOnCloseTrigger={onCloseTrigger}
                     modalOpen={open}
                  />
               )}
            </ModalRoute>
         </Switch>
         {/* Create Session Modal. We don't use route for Session creation right now
          as it requires some re-working of this component to do so
         (because of how selectedDateKey works with Routes) */}
         <Modal
            borderRadius="5px"
            width="270px"
            onCloseTrigger={() => setCreateSessionModalIsOpen(false)}
            open={createSessionModalIsOpen}>
            <SessionCreate
               initiativeTypeId={sessionInitiativeType.id}
               onCancelButtonClick={() => setCreateSessionModalIsOpen(false)}
               onCreate={() => setCreateSessionModalIsOpen(false)}
               parentInitiativeId={event.id}
            />
         </Modal>
         {/* Delete Initiative Confirmation Modal */}
         {initiativeForDeletion && (
            <Modal
               borderRadius="5px"
               width="90%[a-b] 300px[c-f]"
               onCloseTrigger={() => setInitiativeForDeletion(null)}
               open={initiativeForDeletion}>
               <ObjectDeleteConfirm
                  activeSubInitiativeTypeNames={initiativeForDeletion.activeSubInitiativeTypeNames}
                  initiativeId={initiativeForDeletion.id}
                  initiativeName={initiativeForDeletion.name}
                  onCancelButtonClick={() => setInitiativeForDeletion(null)}
                  onDelete={() => setInitiativeForDeletion(null)}
               />
            </Modal>
         )}
      </>
   )
}

InitiativeSchedule.propTypes = {
   paddingLeft: PropTypes.string,
   paddingRight: PropTypes.string,
   showMobileBackButton: PropTypes.bool
}

InitiativeSchedule.defaultProps = {
   paddingLeft: undefined,
   paddingRight: undefined,
   showMobileBackButton: false
}

export default InitiativeSchedule
